import React, { useState } from "react";
import {
  Modal,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Alert,
  message,
  Result,
} from "antd";
import { CopyOutlined, MailOutlined, PlusOutlined } from "@ant-design/icons";
import { IconUploader } from "./IconUploader";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const PLATFORMS_OPTIONS = [
  { value: "quest", label: "Quest (Android APK)" },
  { value: "webgl", label: "WebGL desktop" },
  { value: "standalone", label: "Standalone (Win/Mac/Linux)" },
  { value: "aframe", label: "A-Frame (compat futura)" },
];

/**
 * Wizard 3 pasos para crear un LiveStreamGame.
 *
 * Paso 1: form con label, description, platforms, icono.
 * Paso 2: confirmacion + preview del gameKey que se generara.
 * Paso 3: success con el gameKey copiable + plantilla email.
 *
 * Props:
 *   - open: bool
 *   - onClose()
 *   - onCreate(payload) -> Promise<game> (sin icon). El wizard hace upload despues.
 *   - onUploadIcon(gameKey, file) -> Promise<publicUrl>
 *   - onUpdateGame(gameId, patch) -> Promise<game> (para anyadir iconUrl tras upload)
 *   - generatePreview(label) -> string|null (slug preview, opcional)
 */
export function LiveGameCreateWizard({
  open,
  onClose,
  onCreate,
  onUploadIcon,
  onUpdateGame,
}) {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [createdGame, setCreatedGame] = useState(null);
  const [iconState, setIconState] = useState(null); // { file } | null

  const handleClose = () => {
    form.resetFields();
    setStep(0);
    setCreatedGame(null);
    setIconState(null);
    onClose();
  };

  const goToConfirm = async () => {
    try {
      await form.validateFields();
      setStep(1);
    } catch {
      // antd ya muestra errores
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // getFieldsValue(true) devuelve TODO el store, incluidos campos
      // desmontados. El Form solo se renderiza en step 0, asi que en el
      // submit (step 1) los Form.Item ya no estan montados y un
      // getFieldsValue() sin args devolveria {} -> label undefined -> crash.
      const values = form.getFieldsValue(true);
      const payload = {
        label: values.label.trim(),
        description: values.description?.trim() || "",
        platforms: values.platforms || ["quest"],
      };
      const game = await onCreate(payload);

      // Si el admin seleccionó imagen, subirla y patch.
      if (iconState && iconState.file) {
        try {
          const publicUrl = await onUploadIcon(game.gameKey, iconState.file);
          const updated = await onUpdateGame(game.id, { iconUrl: publicUrl });
          setCreatedGame(updated);
        } catch (e) {
          message.warning(
            "Juego creado, pero la imagen no se pudo subir. " +
              "Puedes anyadirla despues editando.",
          );
          setCreatedGame(game);
        }
      } else {
        setCreatedGame(game);
      }
      setStep(2);
    } catch (e) {
      message.error(e?.message || "Error creando juego");
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = () => {
    if (!createdGame) return;
    navigator.clipboard.writeText(createdGame.gameKey);
    message.success("gameKey copiado");
  };

  const copyEmail = () => {
    if (!createdGame) return;
    const body =
      `Hola,\n\n` +
      `Tu juego "${createdGame.label}" esta dado de alta en Educa360 Live.\n\n` +
      `gameKey: ${createdGame.gameKey}\n\n` +
      `Instrucciones de integracion (Unity 6.0 LTS o superior).\n` +
      `Se instalan dos packages, los dos desde el Package Manager de Unity\n` +
      `(NO hace falta editar ningun archivo a mano). Ambos repos son publicos.\n\n` +
      `1. Instala la dependencia com.unity.webrtc (fork):\n` +
      `   Window -> Package Manager -> boton + -> Add package from git URL...\n` +
      `   https://github.com/GitEducaverse2024/com.unity.webrtc.git#3.0.0-pre.4-edu360\n` +
      `   Espera a que Unity descargue y recompile.\n` +
      `2. Instala el SDK Educa360 Live (mismo boton + -> Add package from git URL...):\n` +
      `   https://github.com/GitEducaverse2024/com.educa360.live.git#v1.0.0\n` +
      `   Si ves errores rojos momentaneos mientras descarga, es normal: se van\n` +
      `   al terminar de recompilar. No cierres ni reinicies Unity.\n` +
      `3. Tools -> Educa360 Live -> Setup Wizard (te guia el resto).\n` +
      `4. En el LiveStreamConfig generado, pega el gameKey de arriba.\n` +
      `5. Play. Veras un code de 5 caracteres para introducir en live.educa360.net.\n\n` +
      `Cualquier duda, escribeme.\n` +
      `Saludos`;
    navigator.clipboard.writeText(body);
    message.success("Email copiado al portapapeles");
  };

  const createAnother = () => {
    form.resetFields();
    setStep(0);
    setCreatedGame(null);
    setIconState(null);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Nuevo LiveGame"
      width={680}
      footer={null}
      destroyOnClose
    >
      <Steps
        size="small"
        current={step}
        items={[{ title: "Datos" }, { title: "Confirmar" }, { title: "Hecho" }]}
        style={{ marginBottom: 24 }}
      />

      {step === 0 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ platforms: ["quest"] }}
        >
          <Form.Item
            name="label"
            label="Nombre del juego"
            rules={[
              { required: true, message: "Nombre requerido" },
              { min: 3, message: "Minimo 3 chars" },
              { max: 80, message: "Maximo 80 chars" },
            ]}
          >
            <Input placeholder="Ej: Pelican VR" autoFocus />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descripcion (opcional)"
            rules={[{ max: 500, message: "Maximo 500 chars" }]}
          >
            <TextArea
              rows={3}
              placeholder="Breve descripcion para identificar el juego en el panel."
            />
          </Form.Item>
          <Form.Item
            name="platforms"
            label="Plataformas soportadas"
            rules={[{ required: true, message: "Selecciona al menos una" }]}
          >
            <Select
              mode="multiple"
              placeholder="Quest, WebGL, Standalone..."
              options={PLATFORMS_OPTIONS}
            />
          </Form.Item>
          <Form.Item label="Icono (opcional)">
            <IconUploader value={iconState} onChange={setIconState} />
          </Form.Item>
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={handleClose}>Cancelar</Button>
              <Button type="primary" onClick={goToConfirm}>
                Siguiente
              </Button>
            </Space>
          </div>
        </Form>
      )}

      {step === 1 && (
        <div>
          <Alert
            type="warning"
            showIcon
            message="El gameKey se genera automaticamente al crear y NO se puede cambiar despues."
            description="Los APKs distribuidos con el gameKey lo tienen hardcoded; renombrar lo romperia todo. Confirma que los datos son correctos antes de crear."
            style={{ marginBottom: 16 }}
          />
          <Paragraph>
            <Text strong>Resumen:</Text>
          </Paragraph>
          <ul style={{ lineHeight: 1.8 }}>
            <li>
              <Text strong>Nombre:</Text> {form.getFieldValue("label")}
            </li>
            {form.getFieldValue("description") && (
              <li>
                <Text strong>Descripcion:</Text>{" "}
                {form.getFieldValue("description")}
              </li>
            )}
            <li>
              <Text strong>Plataformas:</Text>{" "}
              {(form.getFieldValue("platforms") || []).join(", ")}
            </li>
            <li>
              <Text strong>Icono:</Text> {iconState ? "Si (se subira)" : "No"}
            </li>
          </ul>
          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Space>
              <Button onClick={() => setStep(0)}>Atras</Button>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleSubmit}
              >
                Crear juego
              </Button>
            </Space>
          </div>
        </div>
      )}

      {step === 2 && createdGame && (
        <Result
          status="success"
          title="Juego creado"
          subTitle={
            <div>
              <Paragraph>
                Comparte el gameKey con el dev del juego. Es lo unico que
                necesita para integrar el SDK.
              </Paragraph>
              <div
                style={{
                  fontSize: 24,
                  fontFamily: "monospace",
                  background: "#fafafa",
                  padding: 12,
                  borderRadius: 6,
                  marginTop: 8,
                  marginBottom: 12,
                  userSelect: "all",
                }}
              >
                {createdGame.gameKey}
              </div>
            </div>
          }
          extra={[
            <Button
              key="copy"
              type="primary"
              icon={<CopyOutlined />}
              onClick={copyKey}
            >
              Copiar gameKey
            </Button>,
            <Button key="email" icon={<MailOutlined />} onClick={copyEmail}>
              Copiar email para el dev
            </Button>,
            <Button
              key="another"
              icon={<PlusOutlined />}
              onClick={createAnother}
            >
              Crear otro
            </Button>,
            <Button key="close" onClick={handleClose}>
              Cerrar
            </Button>,
          ]}
        />
      )}
    </Modal>
  );
}
